'use client';

import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { ElementType } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

type AnimationType = 'word' | 'character';
type AnimationVariant =
  | 'fadeIn'
  | 'blurIn'
  | 'blurInUp'
  | 'blurInDown'
  | 'slideUp'
  | 'slideDown'
  | 'slideUpBlurIn'
  | 'slideDownBlurIn'
  | 'slideLeftBlurIn'
  | 'slideRightBlurIn'
  | 'slideLeft'
  | 'slideRight'
  | 'scaleUp'
  | 'scaleDown'
  | 'flipX'
  | 'flipY'
  | 'skewLeft'
  | 'skewRight'
  | 'skewLeftBlurIn'
  | 'skewRightBlurIn';

interface TextAnimateProps {
  children: string;
  className?: string;
  segmentClassName?: string;
  delay?: number;
  duration?: number;
  stagger?: number;
  as?: ElementType;
  by?: AnimationType;
  startOnView?: boolean;
  once?: boolean;
  animation?: AnimationVariant;
}

const getGsapProps = (
  variant: AnimationVariant,
): { from: gsap.TweenVars; to: gsap.TweenVars } => {
  switch (variant) {
    case 'fadeIn':
      return { from: { opacity: 0, y: 20 }, to: { opacity: 1, y: 0 } };
    case 'blurIn':
      return {
        from: { opacity: 0, filter: 'blur(10px)' },
        to: { opacity: 1, filter: 'blur(0px)' },
      };
    case 'blurInUp':
      return {
        from: { opacity: 0, filter: 'blur(10px)', y: 20 },
        to: { opacity: 1, filter: 'blur(0px)', y: 0 },
      };
    case 'blurInDown':
      return {
        from: { opacity: 0, filter: 'blur(10px)', y: -20 },
        to: { opacity: 1, filter: 'blur(0px)', y: 0 },
      };
    case 'slideUp':
      return { from: { y: 20, opacity: 0 }, to: { y: 0, opacity: 1 } };
    case 'slideDown':
      return { from: { y: -20, opacity: 0 }, to: { y: 0, opacity: 1 } };
    case 'slideLeft':
      return { from: { x: 20, opacity: 0 }, to: { x: 0, opacity: 1 } };
    case 'slideRight':
      return { from: { x: -20, opacity: 0 }, to: { x: 0, opacity: 1 } };
    case 'slideUpBlurIn':
      return {
        from: { y: 20, opacity: 0, filter: 'blur(10px)' },
        to: { y: 0, opacity: 1, filter: 'blur(0px)' },
      };
    case 'slideDownBlurIn':
      return {
        from: { y: -20, opacity: 0, filter: 'blur(10px)' },
        to: { y: 0, opacity: 1, filter: 'blur(0px)' },
      };
    case 'slideLeftBlurIn':
      return {
        from: { x: 20, opacity: 0, filter: 'blur(10px)' },
        to: { x: 0, opacity: 1, filter: 'blur(0px)' },
      };
    case 'slideRightBlurIn':
      return {
        from: { x: -20, opacity: 0, filter: 'blur(10px)' },
        to: { x: 0, opacity: 1, filter: 'blur(0px)' },
      };
    case 'scaleUp':
      return { from: { scale: 0.5, opacity: 0 }, to: { scale: 1, opacity: 1 } };
    case 'scaleDown':
      return { from: { scale: 1.5, opacity: 0 }, to: { scale: 1, opacity: 1 } };
    case 'flipX':
      return { from: { opacity: 0, rotateX: -90 }, to: { opacity: 1, rotateX: 0 } };
    case 'flipY':
      return { from: { opacity: 0, rotateY: 90 }, to: { opacity: 1, rotateY: 0 } };
    case 'skewLeft':
      return {
        from: { opacity: 0, skewX: 45, x: -25 },
        to: { opacity: 1, skewX: 0, x: 0 },
      };
    case 'skewRight':
      return {
        from: { opacity: 0, skewX: -45, x: 25 },
        to: { opacity: 1, skewX: 0, x: 0 },
      };
    case 'skewLeftBlurIn':
      return {
        from: { opacity: 0, skewX: 45, x: -25, filter: 'blur(10px)' },
        to: { opacity: 1, skewX: 0, x: 0, filter: 'blur(0px)' },
      };
    case 'skewRightBlurIn':
      return {
        from: { opacity: 0, skewX: -45, x: 25, filter: 'blur(10px)' },
        to: { opacity: 1, skewX: 0, x: 0, filter: 'blur(0px)' },
      };
    default:
      return { from: { opacity: 0 }, to: { opacity: 1 } };
  }
};

export function TextAnimate({
  children,
  delay = 0,
  duration = 0.7,
  stagger,
  className,
  segmentClassName,
  as: Component = 'p',
  startOnView = false,
  once = false,
  by = 'word',
  animation = 'fadeIn',
}: TextAnimateProps) {
  const containerRef = useRef<HTMLElement | null>(null);
  const segmentsRef = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    const { from, to } = getGsapProps(animation);
    const elems = segmentsRef.current.filter(Boolean) as HTMLElement[];
    const count = elems.length;
    if (!count) return;

    // set initial state
    gsap.set(elems, from);

    // compute final stagger
    const stagAmt = stagger !== undefined ? stagger : duration / count;

    const cfg: gsap.TweenVars = {
      ...to,
      delay,
      duration,
      stagger: stagAmt,
      ease: 'power3.out',
      overwrite: 'auto',
    };
    if (startOnView && containerRef.current) {
      (cfg as gsap.TweenVars).scrollTrigger = {
        trigger: containerRef.current,
        start: 'top 80%',
        once,
      };
    }

    const tween = gsap.to(elems, cfg);
    return () => {
      tween.kill();
      if (startOnView) ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, [children, animation, delay, duration, stagger, startOnView, once, by]);

  // Render logic
  let idx = 0;
  const words = children.split(/(\s+)/);

  return (
    <Component ref={containerRef} className={cn('whitespace-pre-wrap', className)}>
      {by === 'character'
        ? words.map((w, wi) => {
            if (/^\s+$/.test(w)) return w;
            return (
              <span key={wi} style={{ display: 'inline-block' }}>
                {w.split('').map((char, ci) => (
                  <span
                    key={`${wi}-${ci}`}
                    ref={(el) => void (segmentsRef.current[idx++] = el)}
                    className={cn(
                      'inline-block align-middle whitespace-pre will-change-transform',
                      segmentClassName,
                    )}>
                    {char}
                  </span>
                ))}
              </span>
            );
          })
        : // word mode
          words.map((w, wi) =>
            /^\s+$/.test(w) ? (
              w
            ) : (
              <span
                key={wi}
                ref={(el) => void (segmentsRef.current[idx++] = el)}
                className={cn(
                  'inline-block whitespace-pre will-change-transform',
                  segmentClassName,
                )}>
                {w}
              </span>
            ),
          )}
    </Component>
  );
}

export default TextAnimate;
